import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountWebLayout, AccountPageHeader, AccountTabBar } from './AccountWebLayout';
import {
    fetchCoupons,
    formatCouponDiscount,
    formatCouponMinOrder,
} from '../../utils/couponsApi';
import { useToast } from '../../Contexts/ToastContext';

export function Coupons() {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [tab, setTab] = useState('active');
    const [catalog, setCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const list = await fetchCoupons();
                if (!cancelled) setCatalog(list);
            } catch (_e) {
                if (!cancelled) {
                    setError('Could not load coupons. Please try again.');
                    setCatalog([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const activeCoupons = useMemo(() => catalog.filter((c) => !c.expired), [catalog]);
    const expiredCoupons = useMemo(() => catalog.filter((c) => c.expired), [catalog]);
    const coupons = tab === 'active' ? activeCoupons : expiredCoupons;

    const handleCopy = async (code) => {
        try {
            await navigator.clipboard.writeText(code);
            showToast({ message: `Coupon ${code} copied`, variant: 'success' });
        } catch (_e) {
            showToast({ message: code, variant: 'success' });
        }
    };

    return (
        <AccountWebLayout title="Coupons">
            <AccountPageHeader
                title="Your coupons"
                description="Copy a code below and apply it on your cart or order summary."
                action={
                    activeCoupons.length > 0 ? (
                        <button type="button" className="btn-primary--sm" onClick={() => navigate('/cart')}>
                            Go to cart
                        </button>
                    ) : null
                }
            />

            <AccountTabBar
                ariaLabel="Coupon filters"
                activeId={tab}
                onChange={setTab}
                tabs={[
                    { id: 'active', label: 'Available', count: loading ? null : activeCoupons.length },
                    { id: 'expired', label: 'Expired', count: loading ? null : expiredCoupons.length },
                ]}
            />

            {error && (
                <div className="account-web-info-block account-web-info-block--error" role="alert">
                    {error}
                </div>
            )}

            {!loading && (
                <p className="account-web-meta">
                    {coupons.length} {coupons.length === 1 ? 'coupon' : 'coupons'}
                </p>
            )}

            <div className="account-web-list">
                {loading ? (
                    [1, 2, 3].map((n) => <div key={n} className="account-web-skeleton" aria-hidden />)
                ) : coupons.length === 0 ? (
                    <div className="account-web-empty">
                        <p>No {tab === 'active' ? 'available' : 'expired'} coupons right now.</p>
                    </div>
                ) : (
                    coupons.map((coupon) => (
                        <article
                            key={coupon.id}
                            className={`account-web-row${coupon.expired ? ' account-web-row--expired' : ''}`}
                        >
                            <div className="account-web-row__main">
                                <p className="account-web-row__code">{coupon.code}</p>
                                <p className="account-web-row__desc">{coupon.description}</p>
                                <p className="account-web-row__meta">
                                    {formatCouponMinOrder(coupon.minOrder)} · Valid till {coupon.validUntil}
                                </p>
                            </div>
                            <div className="account-web-row__aside">
                                <span className="account-web-row__discount">
                                    {formatCouponDiscount(coupon)}
                                </span>
                                {coupon.expired ? (
                                    <span className="account-web-row__status">Expired</span>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn-outline-primary--soft"
                                        onClick={() => handleCopy(coupon.code)}
                                    >
                                        Copy code
                                    </button>
                                )}
                            </div>
                        </article>
                    ))
                )}
            </div>
        </AccountWebLayout>
    );
}
